import React, { useEffect, useRef, useState } from 'react';
import { Map as LeafletMap, Marker, Popup, ZoomControl } from 'react-leaflet';
import SellingSpot from '../../../models/selling/SellingSpot';
import { Coordinate } from '../../../models/tariff/trafficAreas/Coordinate';
import { LatLngBoundsExpression, LatLngExpression } from 'leaflet';
import { SPLIT_COORDINATE } from '../../../config';
import L, { Marker as LeafletMarker } from 'leaflet';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import LeafletBaseLayerToggle, { LeafletBaseLayer, LeafletLabelsState } from './LeafletBaseLayerToggle';
import LeafletBaseLayers from './LeafletBaseLayers';
import LeafletFullscreenControl from './LeafletFullscreenControl';

const containerStyle: React.CSSProperties = {
    width: '100%',
    height: '260px',
    border: '1px solid transparent',
    borderRadius: '15px'
};

const containerStyleFullView: React.CSSProperties = {
    width: '100%',
    height: '520px'
};

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

interface Props {
    sellingSpots: SellingSpot[];
    activeMarker?: Coordinate;
    fullView?: boolean;
    onActiveMarkerChange?: (coordinate?: Coordinate) => any;
}

function FullscreenBoundsController({
    map,
    sellingSpots,
    hasActiveMarkerRef
}: {
    map: L.Map | null;
    sellingSpots: SellingSpot[];
    hasActiveMarkerRef: React.RefObject<boolean>;
}) {
    const sellingSpotsRef = useRef(sellingSpots);

    useEffect(() => { sellingSpotsRef.current = sellingSpots; }, [sellingSpots]);

    useEffect(() => {
        if (!map)
            return;

        const fitToSpots = () => {
            const spots = sellingSpotsRef.current;
            if (!spots || spots.length === 0) return;
            if (spots.length === 1) {
                const { latitude, longitude } = spots[0].coordinate;
                map.flyTo([latitude, longitude], 12.9, { duration: 1 });
                return;
            }
            const bounds = spots.map(s => [s.coordinate.latitude, s.coordinate.longitude] as LatLngExpression);
            map.flyToBounds(bounds as LatLngBoundsExpression, { padding: [50, 50], duration: 1 });
        };

        const handleFullscreenChange = () => {
            window.setTimeout(() => {
                map.invalidateSize();
                if (document.fullscreenElement || !hasActiveMarkerRef.current) {
                    fitToSpots();
                }
            }, 0);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, [map, hasActiveMarkerRef]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
}

function MapController({
    map,
    activeMarker,
    sellingSpots,
    isPopupOpen
}: {
    map: L.Map | null;
    activeMarker?: Coordinate;
    sellingSpots?: SellingSpot[];
    isPopupOpen: boolean;
}) {
    useEffect(() => {
        if (!map)
            return;

        if (activeMarker) {
            const zoom = 17;
            const markerLatLng: LatLngExpression = [activeMarker.latitude, activeMarker.longitude];

            if (isPopupOpen) {
                const markerPoint = map.project(markerLatLng, zoom);
                markerPoint.y -= 110;
                map.flyTo(map.unproject(markerPoint, zoom), zoom, { duration: 1 });
                return;
            }

            const markerPoint = map.project(markerLatLng, zoom);
            markerPoint.y -= 20;
            map.flyTo(map.unproject(markerPoint, zoom), zoom, { duration: 1 });
            return;
        }

        if (!sellingSpots || sellingSpots.length === 0)
            return;

        if (sellingSpots.length === 1) {
            const { latitude, longitude } = sellingSpots[0].coordinate;
            map.flyTo([latitude, longitude], 12.9, { duration: 1 });
            return;
        }

        const bounds = sellingSpots.map(s => [s.coordinate.latitude, s.coordinate.longitude] as LatLngExpression);
        map.flyToBounds(bounds as LatLngBoundsExpression, { padding: [50, 50], duration: 1 });
    }, [map, activeMarker, sellingSpots, isPopupOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
}

function PopupController({
    openMarkerKey,
    markerRefs
}: {
    openMarkerKey?: string;
    markerRefs: React.MutableRefObject<Record<string, LeafletMarker | null>>;
}) {
    useEffect(() => {
        const openCurrentPopup = () => {
            Object.entries(markerRefs.current).forEach(([key, marker]) => {
                if (!marker)
                    return;

                if (key === openMarkerKey) {
                    marker.openPopup();
                    return;
                }

                marker.closePopup();
            });
        };

        const timeoutId = window.setTimeout(openCurrentPopup, openMarkerKey ? 180 : 0);

        return () => window.clearTimeout(timeoutId);
    }, [openMarkerKey, markerRefs]);

    return null;
}

function MapSpotsLeaflet(props: Props) {
    const [activeBaseLayer, setActiveBaseLayer] = useState<LeafletBaseLayer>('map');
    const [labelsByLayer, setLabelsByLayer] = useState<LeafletLabelsState>({ map: true, satellite: true });
    const [openMarkerKey, setOpenMarkerKey] = useState<string | undefined>(undefined);
    const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
    const mapWrapperRef = useRef<HTMLDivElement>(null);
    const markerRefs = useRef<Record<string, LeafletMarker | null>>({});
    const hasActiveMarkerRef = useRef<boolean>(false);

    useEffect(() => {
        hasActiveMarkerRef.current = !!openMarkerKey || !!props.activeMarker;
    }, [openMarkerKey, props.activeMarker]);

    useEffect(() => {
        const nextOpenMarkerKey = props.activeMarker
            ? `${props.activeMarker.latitude}-${props.activeMarker.longitude}`
            : undefined;

        if (!nextOpenMarkerKey) {
            setOpenMarkerKey(undefined);
            return;
        }

        setOpenMarkerKey(undefined);

        const timeoutId = window.setTimeout(() => {
            setOpenMarkerKey(nextOpenMarkerKey);
        }, 0);

        return () => window.clearTimeout(timeoutId);
    }, [props.activeMarker]);

    const getMarkerContent = (sellingSpot: SellingSpot, markerKey: string) => (
        <>
            <div className="selling-spot-popup__header">
                <div className="selling-spot-popup__title-wrap">
                    <h4>{sellingSpot.name}</h4>
                    <h6 className='mb-3'>{sellingSpot.fullAddress}</h6>
                </div>
                <button
                    type="button"
                    className="selling-spot-popup__close"
                    onClick={() => onMarkerClick(undefined)}
                    aria-label="Zatvori"
                >
                    &times;
                </button>
            </div>
            <div className="selling-spot-popup__body">
                {[...new Set(sellingSpot.workingHours)].map((workingHours, i) => (
                    <p key={i}>{workingHours}</p>
                ))}
                <button
                    type="button"
                    className="selling-spot-popup__hide"
                    onClick={() => setOpenMarkerKey(current => current === markerKey ? undefined : current)}
                >
                    Sakrij
                </button>
            </div>
        </>
    );

    const onMarkerClick = (coordinate?: Coordinate, markerKey?: string) => {
        if (markerKey)
            setOpenMarkerKey(markerKey);

        if (!props.onActiveMarkerChange)
            return;
        props.onActiveMarkerChange(coordinate);
    };

    return (
        <div ref={mapWrapperRef} className="leaflet-map-wrapper">
            <LeafletMap
                center={[SPLIT_COORDINATE.lat, SPLIT_COORDINATE.lng]}
                zoom={12.9}
                style={props.fullView ? containerStyleFullView : containerStyle}
                attributionControl={false}
                zoomControl={false}
                ref={(mapRef: any) => {
                    setMapInstance(mapRef ? mapRef.leafletElement : null);
                }}
            >
                <LeafletBaseLayers activeBaseLayer={activeBaseLayer} labelsByLayer={labelsByLayer} />
                <LeafletFullscreenControl map={mapInstance} fullscreenElement={mapWrapperRef.current} />
                <ZoomControl position="bottomright" />
                <FullscreenBoundsController
                    map={mapInstance}
                    sellingSpots={props.sellingSpots}
                    hasActiveMarkerRef={hasActiveMarkerRef}
                />
                <MapController
                    map={mapInstance}
                    activeMarker={props.activeMarker}
                    sellingSpots={props.sellingSpots}
                    isPopupOpen={!!openMarkerKey}
                />
                <PopupController openMarkerKey={openMarkerKey} markerRefs={markerRefs} />
                {props.sellingSpots.map((sellingSpot) => {
                    const markerKey = `${sellingSpot.coordinate.latitude}-${sellingSpot.coordinate.longitude}`;

                    return (
                        <Marker
                            key={markerKey}
                            ref={(markerRef: any) => {
                                markerRefs.current[markerKey] = markerRef ? markerRef.leafletElement : null;
                            }}
                            position={[sellingSpot.coordinate.latitude, sellingSpot.coordinate.longitude]}
                            onClick={() => onMarkerClick(sellingSpot.coordinate, markerKey)}
                        >
                            {openMarkerKey === markerKey && (
                                <Popup
                                    className="selling-spot-popup"
                                    maxWidth={280}
                                    closeButton={false}
                                    autoPan
                                    autoPanPaddingTopLeft={[24, 24]}
                                    autoPanPaddingBottomRight={[24, 24]}
                                    onClose={() => onMarkerClick(undefined)}
                                >
                                    {getMarkerContent(sellingSpot, markerKey)}
                                </Popup>
                            )}
                        </Marker>
                    );
                })}
            </LeafletMap>
            <LeafletBaseLayerToggle
                activeLayer={activeBaseLayer}
                labelsByLayer={labelsByLayer}
                onLayerChange={setActiveBaseLayer}
                onLabelsChange={(layer, enabled) =>
                    setLabelsByLayer((current) => ({ ...current, [layer]: enabled }))
                }
            />
        </div>
    );
}

export default React.memo(MapSpotsLeaflet);
